
import React, { useState } from 'react';
import { Message as MessageType } from '../types';
import { generateAudio } from '../services/geminiService';
import { audioService } from '../services/audioService';
import MessageBubble from './Message/MessageBubble';
import MessageActions from './Message/MessageActions';

interface MessageProps {
  message: MessageType;
  voice: string;
  speechSpeed: number;
}

const Message: React.FC<MessageProps> = ({ message, voice, speechSpeed }) => {
  const isUser = message.role === 'user';
  const [isSynthesizing, setIsSynthesizing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleSpeak = async () => {
    if (isSynthesizing || isPlaying) return;
    try {
      setIsSynthesizing(true);
      const base64Audio = await generateAudio(message.content, voice);
      if (base64Audio) {
        setIsSynthesizing(false);
        setIsPlaying(true);
        await audioService.playPCM(base64Audio, speechSpeed);
        setIsPlaying(false);
      }
    } catch (error) {
      console.error("TTS failed", error);
      setIsSynthesizing(false);
      setIsPlaying(false);
    }
  };

  const handleDownload = async () => {
    if (isDownloading) return;
    try {
      setIsDownloading(true);
      const base64Audio = await generateAudio(message.content, voice);
      if (base64Audio) {
        const wavBlob = audioService.convertPCMToWav(base64Audio, 24000);
        const url = window.URL.createObjectURL(wavBlob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `lahja_audio_${message.id}.wav`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error("Download failed", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-2 group/msg animate-in slide-in-from-bottom-2 duration-300`}>
      <div className={`max-w-[85%] sm:max-w-[70%] lg:max-w-[60%] flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
        <MessageBubble 
          content={message.content}
          isUser={isUser}
          isPlaying={isPlaying}
        />
        <MessageActions 
          timestamp={message.timestamp}
          isUser={isUser}
          isPlaying={isPlaying}
          isSynthesizing={isSynthesizing}
          isDownloading={isDownloading}
          copied={copied}
          onSpeak={handleSpeak}
          onDownload={handleDownload}
          onCopy={handleCopy}
        />
      </div>
    </div>
  );
};

export default Message;
